import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { mapVehiclePositions } from '../data';

const STATUS_COLOR = {
  active:    'var(--gr)',
  overdue:   'var(--ac)',
  available: 'var(--bl)',
  booked:    'var(--am)',
};

export default function LiveMapPage() {
  const { vehicles, pois, openModal, showToast } = useApp();
  const [mapTab, setMapTab] = useState('vehicles');
  const [selected, setSelected] = useState(null);

  const activeCount = vehicles.filter(v => v.status === 'active').length;

  return (
    <div className="map-layout" style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left sidebar */}
      <div className="map-sidebar" style={{
        width: 240, flexShrink: 0, background: 'var(--sur)', borderRight: '1px solid var(--bd)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Live Tracking</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--gr)', background: 'var(--gr-l)', border: '1px solid var(--gr-b)', padding: '3px 8px', borderRadius: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gr)' }} />Live
            </div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', padding: '3px 8px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 5 }}>
              Updated just now
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--bd)' }}>
          {['vehicles','poi','history'].map(tab => (
            <button
              key={tab}
              className={`map-tab${mapTab === tab ? ' active' : ''}`}
              onClick={() => setMapTab(tab)}
              style={{
                flex: 1, padding: '7px 4px', fontSize: 10, fontWeight: 500,
                cursor: 'pointer', border: 'none', background: 'transparent',
                color: mapTab === tab ? 'var(--ac)' : 'var(--tx3)',
                borderBottom: mapTab === tab ? '2px solid var(--ac)' : '2px solid transparent',
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              {tab === 'vehicles' ? 'Vehicles' : tab === 'poi' ? 'POI' : 'History'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
          {mapTab === 'vehicles' && vehicles.map(v => {
            const pos = mapVehiclePositions[v.id];
            return (
              <div
                key={v.id}
                onClick={() => setSelected(selected === v.id ? null : v.id)}
                style={{
                  padding: '8px 10px', borderRadius: 'var(--r)', cursor: 'pointer', marginBottom: 4,
                  background: selected === v.id ? 'var(--ac-l)' : 'var(--sur2)',
                  border: `1px solid ${selected === v.id ? 'var(--ac-b)' : 'var(--bd)'}`,
                  transition: 'all .12s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{v.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 500 }}>{v.name.split('–')[0].trim()}</div>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{v.plate}</div>
                  </div>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[v.status] || 'var(--tx3)' }} />
                </div>
                {pos && (
                  <div style={{ marginTop: 5, fontSize: 10, color: 'var(--tx2)', display: 'flex', gap: 10 }}>
                    <span>⚡ {pos.speed}</span>
                    <span>↗ {pos.heading}</span>
                  </div>
                )}
              </div>
            );
          })}

          {mapTab === 'poi' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx2)' }}>Points of Interest</div>
                <button className="btn btn-xs btn-primary" onClick={() => openModal('add-poi')}>+ Add POI</button>
              </div>
              {pois.map(p => (
                <div key={p.id} style={{ padding: '7px 10px', borderRadius: 'var(--r)', border: '1px solid var(--bd)', marginBottom: 4, fontSize: 11 }}>
                  <span style={{ marginRight: 6 }}>{p.icon}</span>
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 2 }}>{p.type}</div>
                </div>
              ))}
            </div>
          )}

          {mapTab === 'history' && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx2)', marginBottom: 8 }}>Movement Report</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Select vehicle and date to view movement history.</div>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <select className="fsel" style={{ width: '100%', fontSize: 11 }}>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.icon} {v.name}</option>)}
                </select>
                <input type="date" className="fi" defaultValue="2026-03-14" style={{ fontSize: 11 }} />
                <div style={{ padding: '8px 10px', background: 'var(--sur2)', borderRadius: 'var(--r)', border: '1px solid var(--bd)', fontSize: 11, color: 'var(--tx3)' }}>
                  No movement data available for this date.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map canvas */}
      <div className="map-canvas" style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#d4e8c8' }}>
        {/* Info bar */}
        <div style={{
          position: 'absolute', top: 10, left: 10, zIndex: 10,
          display: 'flex', gap: 6,
        }}>
          <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gr)' }} />
            {activeCount} vehicle{activeCount !== 1 ? 's' : ''} active
          </div>
          <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '4px 10px', fontSize: 11 }}>
            📍 {pois.length} POIs
          </div>
          {selected && (
            <div style={{ background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '4px 10px', fontSize: 11, color: 'var(--ac)', display: 'flex', alignItems: 'center', gap: 5 }}>
              {vehicles.find(v => v.id === selected)?.name}
              <span onClick={() => setSelected(null)} style={{ cursor: 'pointer' }}>×</span>
            </div>
          )}
        </div>

        {/* Map controls */}
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {['+','−','⛶','〰','📍'].map((ctrl, i) => (
            <div
              key={i}
              onClick={() => showToast('Map control: ' + ctrl)}
              style={{
                width: 32, height: 32, background: 'var(--sur)', border: '1px solid var(--bd)',
                borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: i === 3 ? 10 : 14,
              }}
            >{ctrl}</div>
          ))}
        </div>

        {/* SVG Map */}
        <svg style={{ width: '100%', height: '100%', display: 'block' }} viewBox="0 0 900 600" preserveAspectRatio="xMidYMid meet">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#c8dcc8" strokeWidth=".5"/>
            </pattern>
          </defs>
          <rect width="900" height="600" fill="#d4e8c8"/>
          <rect width="900" height="600" fill="url(#grid)"/>

          {/* Water */}
          <ellipse cx="820" cy="500" rx="200" ry="160" fill="#b8d4e8" opacity=".7"/>
          <ellipse cx="850" cy="580" rx="180" ry="100" fill="#b8d4e8" opacity=".8"/>
          <text x="830" y="520" fontSize="11" fill="#5a8aaa" fontFamily="'DM Sans',sans-serif" fontWeight="500">Mediterranean Sea</text>

          {/* Roads */}
          <path d="M 0,280 Q 150,270 300,265 Q 450,260 600,268 Q 700,274 780,290" fill="none" stroke="#f0e8c0" strokeWidth="8"/>
          <path d="M 420,0 Q 415,100 418,200 Q 420,300 415,400 Q 410,480 420,550" fill="none" stroke="#ffe" strokeWidth="6"/>
          <path d="M 0,180 Q 200,175 380,195" fill="none" stroke="#fff" strokeWidth="4"/>
          <path d="M 0,380 Q 250,370 500,375 Q 620,382 720,390" fill="none" stroke="#fff" strokeWidth="4"/>

          {/* Labels */}
          <text x="250" y="250" fontSize="13" fill="#555" fontFamily="'DM Sans',sans-serif" fontWeight="600" opacity=".7">Paphos Town</text>
          <text x="490" y="155" fontSize="10" fill="#666" fontFamily="'DM Sans',sans-serif" opacity=".6">Kato Paphos</text>
          <text x="680" y="155" fontSize="10" fill="#666" fontFamily="'DM Sans',sans-serif" opacity=".6">Airport Area</text>
          <text x="380" y="255" fontSize="9" fill="#a0884a" fontWeight="700">A6</text>

          {/* POI markers */}
          {pois.map(p => (
            <g key={p.id} transform={`translate(${p.x},${p.y})`}>
              <circle r="12" fill="var(--sur)" stroke="var(--bd)" strokeWidth="1.5" opacity=".9"/>
              <text textAnchor="middle" dominantBaseline="central" fontSize="10">{p.icon}</text>
              <text y="18" textAnchor="middle" fontSize="8" fill="#555" fontFamily="'DM Sans',sans-serif">{p.name}</text>
            </g>
          ))}

          {/* Vehicle markers */}
          {vehicles.map(v => {
            const pos = mapVehiclePositions[v.id];
            if (!pos) return null;
            const clr = STATUS_COLOR[v.status] || '#888';
            const isSelected = selected === v.id;
            return (
              <g key={v.id} transform={`translate(${pos.x},${pos.y})`} style={{ cursor: 'pointer' }}
                onClick={() => setSelected(selected === v.id ? null : v.id)}>
                <circle r={isSelected ? 22 : 18} fill={clr} opacity=".15"/>
                <circle r="14" fill={clr} stroke="#fff" strokeWidth="2" filter="url(#shadow)"/>
                <text textAnchor="middle" dominantBaseline="central" fontSize="11">{v.icon}</text>
                <text y="24" textAnchor="middle" fontSize="8" fill="#333" fontFamily="'DM Sans',sans-serif" fontWeight="600">{v.plate}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
